import React, { useState, useEffect, useRef } from 'react';
import { Grid, Typography, Skeleton, Box } from '@mui/material';
import { styled, keyframes, useTheme } from '@mui/system';
import getPlaceholderColors from '../../../../../../../utils/placeholderColors';

const LOADING_SKELETON_COUNT = 3;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
`;

const OutputsContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'isFadingOut',
})(({ theme, isFadingOut }) => ({
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '6px',
  padding: '6px',
  marginTop: '6px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
    : '0px 4px 12px rgba(0, 0, 0, 0.08)',
  zIndex: 1000,
  width: '280px',
  animation: isFadingOut ? `${fadeOut} 0.3s ease-out forwards` : `${fadeIn} 0.3s ease-out`,
}));

const PlaceholderBox = styled('div')(({ theme, color, borderColor, textColor, icon, copied, copyIcon }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    backgroundColor: copied ? (isDark ? '#052e16' : '#dcfce7') : color,
    border: `1px solid ${copied ? '#16a34a' : (isDark ? `${borderColor}66` : borderColor)}`,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 6px',
    borderRadius: '5px',
    whiteSpace: 'nowrap',
    fontSize: '11px',
    fontWeight: 500,
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    letterSpacing: '-0.01em',
    color: copied ? '#16a34a' : (textColor || theme.palette.text.primary),
    marginBottom: '3px',
    marginRight: '6px',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.2)' : '0 1px 2px rgba(0,0,0,0.04)',
    '&:hover': {
      backgroundColor: copied
        ? (isDark ? '#052e16' : '#dcfce7')
        : (isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6'),
      borderColor: copied ? '#16a34a' : theme.palette.text.disabled,
      boxShadow: isDark ? '0 2px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.08)',
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&::before': {
      content: '""',
      background: copied ? `url(${copyIcon})` : `url(${icon})`,
      backgroundSize: 'cover',
      width: '12px',
      height: '12px',
      flexShrink: 0,
      transition: 'all 0.2s ease',
      transform: copied ? 'scale(1.1)' : 'scale(1)',
    },
  };
});

const OutputsTitle = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  marginBottom: '6px',
  color: theme.palette.text.secondary,
  letterSpacing: '0.025em',
}));

const NoOutputsText = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '8px 0',
}));

const LoadingSkeletons = () => (
  <Grid container direction="column" spacing={0.375}>
    {Array.from({ length: LOADING_SKELETON_COUNT }).map((_, index) => (
      <Grid item key={index}>
        <Skeleton
          variant="rectangular"
          height={20}
          sx={{
            borderRadius: '4px',
            backgroundColor: (theme) => theme.palette.action.hover,
          }}
        />
      </Grid>
    ))}
  </Grid>
);

const COPIED_ICON_TIMEOUT_MS = 800;
const ComponentOutputsDisplay = ({ showOutputs, loadingOutputs, outputPlaceholders }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const timeoutRef = useRef(null);
  const fadeOutTimeoutRef = useRef(null);

  const copyIcon =
    "\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2316a34a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='9' y='9' width='13' height='13' rx='2' ry='2'%3E%3C/rect%3E%3Cpath d='M5,15H4a2,2 0 0,1 -2,-2V4a2,2 0 0,1 2,-2H13a2,2 0 0,1 2,2v1'%3E%3C/path%3E%3C/svg%3E\"";

  useEffect(() => {
    if (copiedIndex !== null) {
      const timer = setTimeout(() => {
        setCopiedIndex(null);
      }, COPIED_ICON_TIMEOUT_MS);

      return () => clearTimeout(timer);
    }
  }, [copiedIndex]);

  useEffect(() => {
    if (showOutputs) {
      setHasInteracted(true);
      setIsVisible(true);
      setIsFadingOut(false);

      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
    } else if (!showOutputs && hasInteracted) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        setIsFadingOut(true);
        fadeOutTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          setIsFadingOut(false);
        }, 300);
      }, 250);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (fadeOutTimeoutRef.current) clearTimeout(fadeOutTimeoutRef.current);
    };
  }, [showOutputs, hasInteracted]);

  const handleCopyToClipboard = async (placeholderText, index) => {
    try {
      await navigator.clipboard.writeText(placeholderText);
      setCopiedIndex(index);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  if (!isVisible && !isFadingOut) return null;

  return (
    <OutputsContainer isFadingOut={isFadingOut}>
      {loadingOutputs ? (
        <>
          <OutputsTitle>Outputs</OutputsTitle>
          <LoadingSkeletons />
        </>
      ) : outputPlaceholders.length > 0 ? (
        <>
          <OutputsTitle>Outputs</OutputsTitle>
          <Grid container spacing={0.375}>
            {outputPlaceholders.map((placeholder, index) => {
              const colors = getPlaceholderColors(placeholder.type, placeholder.length, isDarkMode);
              const placeholderText = `\${${placeholder.key}}`;
              const isCopied = index === copiedIndex;

              return (
                <Grid item key={index}>
                  <PlaceholderBox
                    color={colors.hover}
                    borderColor={colors.default}
                    textColor={colors.text}
                    icon={colors.icon}
                    copied={isCopied}
                    onClick={() => handleCopyToClipboard(placeholderText, index)}
                    title={isCopied ? 'Copied!' : 'Click to copy'}
                    copyIcon={copyIcon}
                  >
                    {placeholderText}
                  </PlaceholderBox>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : (
        <NoOutputsText>No outputs</NoOutputsText>
      )}
    </OutputsContainer>
  );
};

export default ComponentOutputsDisplay;
